import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import type { EmailMentionableUsersResponse } from '../useData';
import { emailPartitionAtSearch } from '../utils/emailPartitionAtQuery';
import { buildUserMentionInsertText, getAtomicMentionRanges, resolveMentionDeleteRange } from '../utils/mentionUtils';
import { computeMentionMenuStyle, type MentionMenuPlacement } from '../utils/textareaCaret';

type MentionKind = 'user' | 'assignee' | 'teammates' | 'all' | 'here';

interface MentionOption {
  kind: MentionKind;
  id?: number;
  label: string;
  detail?: string;
  insertText: string;
  disabled?: boolean;
}

interface MentionTextareaProps {
  emailId: number;
  partitionAt?: string;
  mentionData?: EmailMentionableUsersResponse | null;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  rows?: number;
  disabled?: boolean;
  /** Use above modal layers when the textarea is inside a drawer/dialog */
  menuZIndex?: number;
}

function getActiveMention(
  text: string,
  cursor: number,
): { start: number; end: number; query: string } | null {
  const before = text.slice(0, cursor);
  const atIndex = before.lastIndexOf('@');
  if (atIndex < 0) return null;
  const query = before.slice(atIndex + 1);
  if (/\s/.test(query)) return null;
  return { start: atIndex, end: cursor, query };
}

const MentionTextarea: React.FC<MentionTextareaProps> = ({
  emailId,
  partitionAt,
  mentionData: mentionDataProp,
  value,
  onChange,
  placeholder,
  className,
  rows = 3,
  disabled = false,
  menuZIndex = Z_INDEX.portaledDropdown,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation('emails');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionDataLocal, setMentionDataLocal] = useState<EmailMentionableUsersResponse | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const mentionData = mentionDataProp ?? mentionDataLocal;
  const [activeMention, setActiveMention] = useState<{ start: number; end: number; query: string } | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [menuPlacement, setMenuPlacement] = useState<MentionMenuPlacement>('below');
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);

  const partitionQuery = emailPartitionAtSearch(partitionAt);

  useEffect(() => {
    if (mentionDataProp != null) return;
    let cancelled = false;
    setLoadingUsers(true);
    api
      .get<EmailMentionableUsersResponse>(`/emails/${emailId}/mentionable-users${partitionQuery}`)
      .then((res) => {
        if (!cancelled) setMentionDataLocal(res.data);
      })
      .catch(() => {
        if (!cancelled) setMentionDataLocal({ users: [], assignee: null });
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [emailId, mentionDataProp, partitionQuery]);

  const options = useMemo((): MentionOption[] => {
    if (!activeMention) return [];
    const q = activeMention.query.toLowerCase();

    const allSpecials: MentionOption[] = [
      {
        kind: 'teammates',
        label: t('mentionTeammates'),
        detail: t('mentionTeammatesDetail'),
        insertText: '@teammates',
      },
      {
        kind: 'assignee',
        label: t('mentionAssignee'),
        detail: mentionData?.assignee
          ? mentionData.assignee.name
          : t('mentionAssigneeEmpty'),
        insertText: '@assignee',
        disabled: !mentionData?.assignee,
      },
      {
        kind: 'all',
        label: t('mentionAll'),
        detail: t('mentionAllDetail'),
        insertText: '@all',
      },
      {
        kind: 'here',
        label: t('mentionHere'),
        detail: t('mentionHereDetail'),
        insertText: '@here',
      },
    ];
    const specials = allSpecials.filter(
      (opt) => !q || opt.label.toLowerCase().includes(q) || opt.insertText.toLowerCase().includes(q),
    );

    const users = (mentionData?.users ?? [])
      .filter((user) => {
        if (!q) return true;
        return (
          user.name.toLowerCase().includes(q) ||
          user.email.toLowerCase().includes(q)
        );
      })
      .map((user) => ({
        kind: 'user' as const,
        id: user.id,
        label: user.name,
        detail: user.email,
        insertText: buildUserMentionInsertText(user.name),
      }));

    return [...specials, ...users];
  }, [activeMention, mentionData, t]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [options.length, activeMention?.query]);

  const openMentionMenu = useCallback(
    (el: HTMLTextAreaElement, caret: number) => {
      const mention = getActiveMention(el.value, caret);
      setActiveMention(mention);
      if (!mention) {
        setMenuStyle(null);
        return;
      }
      const { placement, style } = computeMentionMenuStyle(el, caret);
      setMenuPlacement(placement);
      setMenuStyle({ ...style, ...zStyle(menuZIndex) });
    },
    [menuZIndex],
  );

  const updateMenuPosition = useCallback(() => {
    const el = textareaRef.current;
    if (!el || !activeMention) {
      return;
    }
    const caretIndex = el.selectionStart ?? activeMention.end;
    const { placement, style } = computeMentionMenuStyle(el, caretIndex);
    setMenuPlacement(placement);
    setMenuStyle({ ...style, ...zStyle(menuZIndex) });
  }, [activeMention, menuZIndex]);

  const syncActiveMention = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      setActiveMention(null);
      setMenuStyle(null);
      return;
    }
    openMentionMenu(el, el.selectionStart ?? el.value.length);
  }, [openMentionMenu]);

  useEffect(() => {
    if (!activeMention) {
      setMenuStyle(null);
      return;
    }
    updateMenuPosition();
    const onLayout = () => updateMenuPosition();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [activeMention, options.length, updateMenuPosition]);

  const applyMention = useCallback(
    (option: MentionOption) => {
      if (!activeMention || option.disabled) return;
      const next = `${value.slice(0, activeMention.start)}${option.insertText} ${value.slice(activeMention.end)}`;
      onChange(next);
      setActiveMention(null);
      setMenuStyle(null);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        const pos = activeMention.start + option.insertText.length + 1;
        el.focus();
        el.setSelectionRange(pos, pos);
      });
    },
    [activeMention, onChange, value],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const el = event.currentTarget;
    const selStart = el.selectionStart ?? 0;
    const selEnd = el.selectionEnd ?? 0;

    if (event.key === 'Backspace' || event.key === 'Delete') {
      const ranges = getAtomicMentionRanges(value, mentionData?.users ?? []);
      const deleteRange = resolveMentionDeleteRange(selStart, selEnd, ranges, event.key);
      if (deleteRange) {
        event.preventDefault();
        const next = `${value.slice(0, deleteRange.start)}${value.slice(deleteRange.end)}`;
        onChange(next);
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(deleteRange.start, deleteRange.start);
          openMentionMenu(el, deleteRange.start);
        });
        return;
      }
    }

    if (!activeMention || options.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((i) => (i + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((i) => (i - 1 + options.length) % options.length);
    } else if (event.key === 'Enter' && !event.shiftKey) {
      const selected = options[highlightIndex];
      if (selected && !selected.disabled) {
        event.preventDefault();
        applyMention(selected);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setActiveMention(null);
      setMenuStyle(null);
    }
  };

  const showMenu = Boolean(activeMention && menuStyle);

  const menuContent = showMenu ? (
    <div
      className={cn(
        'overflow-y-auto rounded-lg border shadow-lg',
        colors.surface,
        colors.border,
        menuPlacement === 'above' ? 'origin-bottom' : 'origin-top',
      )}
      style={menuStyle ?? undefined}
      role="listbox"
      aria-label={t('mentionListAria')}
    >
      {loadingUsers && mentionDataProp == null ? (
        <p className={cn('px-3 py-2 text-sm', colors.textMuted)}>{t('mentionLoading')}</p>
      ) : options.length === 0 ? (
        <p className={cn('px-3 py-2 text-sm', colors.textMuted)}>{t('mentionNoMatches')}</p>
      ) : (
        <ul className="py-1">
          {options.map((option, index) => (
            <li key={`${option.kind}-${option.id ?? option.insertText}`}>
              <button
                type="button"
                disabled={option.disabled}
                onMouseDown={(e) => {
                  e.preventDefault();
                  applyMention(option);
                }}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm',
                  option.disabled && 'opacity-50 cursor-not-allowed',
                  index === highlightIndex && !option.disabled
                    ? 'bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-100'
                    : cn(colors.text, colors.interactiveHover),
                )}
              >
                <div className="font-medium">{option.label}</div>
                {option.detail ? (
                  <div className={cn('text-xs truncate', colors.textMuted)}>{option.detail}</div>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        disabled={disabled}
        rows={rows}
        placeholder={placeholder}
        onChange={(e) => {
          const el = e.target;
          const newValue = el.value;
          const caret = el.selectionStart ?? newValue.length;
          onChange(newValue);
          requestAnimationFrame(() => {
            openMentionMenu(el, caret);
          });
        }}
        onClick={() => {
          syncActiveMention();
          requestAnimationFrame(updateMenuPosition);
        }}
        onKeyUp={() => {
          syncActiveMention();
          requestAnimationFrame(updateMenuPosition);
        }}
        onKeyDown={handleKeyDown}
        onScroll={() => {
          if (activeMention) updateMenuPosition();
        }}
        onBlur={() => {
          window.setTimeout(() => {
            setActiveMention(null);
            setMenuStyle(null);
          }, 150);
        }}
        className={className}
      />
      {menuContent && typeof document !== 'undefined' ? createPortal(menuContent, document.body) : null}
    </div>
  );
};

export default MentionTextarea;
